import { EarningsInsight } from '@api';
import { WithNAFallback } from '@design/components/table';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { EarningsTableItem } from '@terminal/views/theme-overview/tab-content/earnings/Earnings';
import { CompanyCell } from '@terminal/views/theme-overview/tab-content/overview/top-companies-table/cells/CompanyCell';
import { DateCell } from '@terminal/widgets/earnings/components/date-cell/DateCell';
import { NumberCell } from '@terminal/widgets/earnings/components/number-cell/NumberCell';
import { PriceCell } from '@terminal/widgets/earnings/components/price-cell/PriceCell';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { TFunction } from 'i18next';

import styles from '../EarningsTable.module.scss';

export const COMPANY_COLUMN_ID = 'company';
export const SCHEDULED_DATE_COLUMN_ID = 'scheduled-date';

const SORT_UNDEFINED = 'last';

export const getColumns = ({
  t,
  earningsInsightByEarningsId,
  isEarningsInsightsFetching,
}: {
  t: TFunction;
  earningsInsightByEarningsId?: Map<string, EarningsInsight>;
  isEarningsInsightsFetching: boolean;
}): ColumnDef<EarningsTableItem>[] => {
  const fallbackTooltip = t(
    'widget:themeOverview.tabs.earnings.earningsDataNotAvailable'
  );

  const createPriceCell = (
    props: CellContext<EarningsTableItem, any>,
    fallbackTooltip: string
  ) => {
    const isSupported = props.row.original.isSupported;
    return (
      <PriceCell
        value={props.getValue()}
        useFallbackDash={isSupported}
        fallbackTooltip={isSupported ? undefined : fallbackTooltip}
      />
    );
  };

  const createNumberCell = ({
    props,
    fallbackTooltip,
    percentage,
  }: {
    props: CellContext<EarningsTableItem, any>;
    fallbackTooltip: string;
    percentage?: boolean;
  }) => {
    const isSupported = props.row.original.isSupported;
    return (
      <NumberCell
        value={props.getValue()}
        percentage={percentage}
        useFallbackDash={isSupported}
        fallbackTooltip={isSupported ? undefined : fallbackTooltip}
        includePercentSign={false}
      />
    );
  };

  return [
    {
      header: ' ',
      columns: [
        {
          id: 'rank',
          header: t('widget:earnings.rank'),
          cell: props => <TopRankIcon rank={props.getValue()} />,
          accessorFn: row => row.rank,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: COMPANY_COLUMN_ID,
          header: t('widget:earnings.company'),
          cell: props => (
            <div className={styles.companyCellWrapper}>
              <CompanyCell
                entityTag={props.row.original.entity_tag}
                variant="secondary"
                earningsInsight={earningsInsightByEarningsId?.get(
                  props.row.original.id
                )}
                isEarningsInsightsFetching={isEarningsInsightsFetching}
              />
            </div>
          ),
        },
        {
          id: SCHEDULED_DATE_COLUMN_ID,
          header: t('widget:earnings.scheduledDate'),
          cell: props => (
            <div className={styles.styledDateCell}>
              {!!props.getValue() ? (
                <DateCell date={props.getValue()} />
              ) : (
                <WithNAFallback value={null} tooltip={fallbackTooltip} />
              )}
            </div>
          ),
          accessorFn: row => row.reporting_date,
          sortUndefined: SORT_UNDEFINED,
        },
      ],
    },
    {
      header: t('widget:earnings.eps'),
      columns: [
        {
          id: 'eps-reported',
          header: t('widget:earnings.reported'),
          cell: props => createPriceCell(props, fallbackTooltip),
          accessorFn: row => row.eps.value,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'eps-surprise-dollar',
          header: t('widget:earnings.surprise', { suffix: '$' }),
          cell: props => createNumberCell({ props, fallbackTooltip }),
          accessorFn: row => row.eps.surprise,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'eps-surprise-percentage',
          header: t('widget:earnings.surprise', { suffix: '%' }),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => row.eps.surprise_percentage,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
      ],
    },
    {
      header: t('widget:earnings.revenue'),
      columns: [
        {
          id: 'revenue-reported',
          header: t('widget:earnings.reported'),
          cell: props => createPriceCell(props, fallbackTooltip),
          accessorFn: row => row.revenue.value,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'revenue-surprise-dollar',
          header: t('widget:earnings.surprise', { suffix: '$' }),
          cell: props => createNumberCell({ props, fallbackTooltip }),
          accessorFn: row => row.revenue.surprise,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'revenue-surprise-percentage',
          header: t('widget:earnings.surprise', { suffix: '%' }),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => row.revenue.surprise_percentage,
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
      ],
    },
    {
      header: t('widget:earnings.percentMove'),
      columns: [
        {
          id: 'return-one-day',
          header: t('widget:earnings.1d'),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => handlePriceData(row.price, 'd1'),
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'return-two-days',
          header: t('widget:earnings.2d'),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => handlePriceData(row.price, 'd2'),
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'return-one-week',
          header: t('widget:earnings.1w'),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => handlePriceData(row.price, 'w1'),
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'return-two-weeks',
          header: t('widget:earnings.2w'),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => handlePriceData(row.price, 'w2'),
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
        {
          id: 'return-one-month',
          header: t('widget:earnings.1m'),
          cell: props =>
            createNumberCell({ props, fallbackTooltip, percentage: true }),
          accessorFn: row => handlePriceData(row.price, 'm1'),
          sortUndefined: SORT_UNDEFINED,
          sortingFn: 'basic',
        },
      ],
    },
  ];
};
